import React from 'react';
import Alert from 'react-bootstrap/Alert';
import { Button, Col, Form, Row } from 'react-bootstrap';
import './Login.css';
import { ContentService } from '../services/ContentService';

const stripHtml = (html: string): string => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    return textContent.replace(/Error/i, '').trim();
};

export default class Login extends React.Component<
    {
        contentService: ContentService;
        onLoggedIn: (loginData?: { username: string; name: string; email: string; csrfToken?: string }) => void;
        onLoggedOut: () => void;
    },
    {
        loginData?: {
            username: string;
            name: string;
            email: string;
            csrfToken?: string;
        };
        loginMessage?: string;
        username: string;
        password: string;
    }
> {
    constructor(props) {
        super(props);
        // Try to restore loginData from localStorage
        let loginData: { username: string; name: string; email: string; csrfToken?: string } | undefined = undefined;
        
        // Check if we're in browser environment (not during SSR/build)
        if (typeof window !== 'undefined' && window.localStorage) {
            try {
                const stored = localStorage.getItem('h5p_login_data');
                if (stored) {
                    const parsed = JSON.parse(stored);
                    if (parsed.username && parsed.name && parsed.email) {
                        loginData = {
                            username: parsed.username,
                            name: parsed.name,
                            email: parsed.email,
                            csrfToken: parsed.csrfToken
                        };
                        // Restore CSRF token if available
                        if (parsed.csrfToken) {
                            this.props.contentService.setCsrfToken(parsed.csrfToken);
                        }
                    }
                }
            } catch (error) {
                console.error('Error loading login data from localStorage:', error);
            }
        }
        
        this.state = {
            username: '',
            password: '',
            loginData
        };
    }

    private login = () => {
        const { username, password } = this.state;

        if (!username || !password) {
            this.setState({
                loginMessage: 'Username and password are required.'
            });
            return;
        }

        fetch(process.env.REACT_APP_H5P_SERVER_API_URL + '/login', {
            method: 'POST',
            headers: {
                Accept: 'application/json, text/plain, */*',
                'Content-type': 'application/json'
            },
            credentials: 'include', // Include cookies for session
            body: JSON.stringify({ username, password })
        })
            .then(async (res) => {
                if (res.status === 200) {
                    const loginData = await res.json();
                    this.setState({
                        ...this.state,
                        loginData,
                        loginMessage: undefined
                    });
                    if (loginData.csrfToken) {
                        this.props.contentService.setCsrfToken(loginData.csrfToken);
                    }
                    // Pass loginData to onLoggedIn callback
                    this.props.onLoggedIn({
                        username: loginData.username,
                        name: loginData.name,
                        email: loginData.email,
                        csrfToken: loginData.csrfToken
                    });
                } else {
                    this.setState({
                        ...this.state,
                        loginData: undefined,
                        loginMessage: stripHtml(await res.text())
                    });
                    this.props.contentService.setCsrfToken(undefined);
                    this.props.onLoggedOut();
                }
            })
            .catch((reason) => {
                this.setState({
                    ...this.state,
                    loginData: undefined,
                    loginMessage: reason.message || 'Login failed.'
                });
                this.props.contentService.setCsrfToken(undefined);
                this.props.onLoggedOut();
            });
    };

    logout = () => {
        const apiUrl = process.env.REACT_APP_H5P_SERVER_API_URL || '';
        fetch(apiUrl + '/logout', {
            method: 'POST',
            headers: {
                Accept: 'application/json, text/plain, */*',
                'Content-type': 'application/json',
                'CSRF-Token': this.props.contentService.getCsrfToken() ?? ''
            },
            credentials: 'include', // Include cookies for session
            body: JSON.stringify({})
        })
            .then(() => {
                this.setState({
                    ...this.state,
                    loginData: undefined,
                    loginMessage: undefined,
                    username: '',
                    password: ''
                });
                this.props.contentService.setCsrfToken(undefined);
                this.props.onLoggedOut();
            })
            .catch((reason) => {
                this.setState({
                    ...this.state,
                    loginData: undefined,
                    loginMessage: `Error logging out: ${reason}`
                });
                this.props.contentService.setCsrfToken(undefined);
                this.props.onLoggedOut();
            });
    };

    handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        this.setState(
            (prevState) =>
                ({
                    ...prevState,
                    [name]: value // Dynamically update the field by name
                }) as Pick<typeof this.state, keyof typeof this.state>
        );
    };

    render() {
        return (
            <React.Fragment>
                {!this.state.loginData ? (
                    <Form className="custom-form border rounded">
                        <div className="text-center">
                            <img
                                src="/vmblogo.jpg"
                                alt="Logo"
                                style={{ maxWidth: '150px', height: 'auto' }}
                            />
                        </div>
                        <Form.Group controlId="formUsername">
                            <Form.Label>Username</Form.Label>
                            <Form.Control
                                type="text"
                                name="username"
                                value={this.state.username}
                                onChange={this.handleInputChange}
                                placeholder="Enter username"
                            />
                        </Form.Group>
                        <Form.Group controlId="formPassword">
                            <Form.Label>Password</Form.Label>
                            <Form.Control
                                type="password"
                                name="password"
                                value={this.state.password}
                                onChange={this.handleInputChange}
                                placeholder="Enter password"
                            />
                        </Form.Group>
                        <Button className="mt-2" onClick={this.login} variant="primary">
                            Login
                        </Button>
                    </Form>
                ) : (
                    <Row className="align-items-center">
                        <Col>{this.state.loginData.name}</Col>
                        <Col>
                            <Button onClick={this.logout}>Logout</Button>
                        </Col>
                    </Row>
                )}
                {this.state.loginMessage && (
                    <Alert className="mt-1" variant="danger">
                        Login error: {this.state.loginMessage}
                    </Alert>
                )}
            </React.Fragment>
        );
    }
}
